import React, { useEffect, useRef, useState } from "react";
import { animate as fmAnimate } from "framer-motion";

/**
 * Count-up animation for KPI numbers. Falls back to instant render
 * when prefers-reduced-motion is set or value is non-numeric.
 *
 * Props:
 *   value      number  the target value to animate to
 *   duration   number  seconds (default 0.8)
 *   format     fn      formatter applied to current value (default Math.round + Intl)
 *   prefix     string  e.g. "₹ "
 *   suffix     string  e.g. "%"
 *   className  string
 *   testid     string
 */
export function AnimatedNumber({
  value,
  duration = 0.8,
  format,
  prefix = "",
  suffix = "",
  className = "",
  testid,
}) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);

  const reduce =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    const target = Number(value);
    if (!Number.isFinite(target)) {
      setDisplay(value);
      return;
    }
    if (reduce) {
      setDisplay(target);
      return;
    }
    const controls = fmAnimate(0, target, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(v),
    });
    return () => controls.stop();
  }, [value, duration, reduce]);

  const fmt = format
    ? format(display)
    : Number.isFinite(Number(display))
    ? Math.round(Number(display)).toLocaleString("en-IN")
    : String(display);

  return (
    <span ref={ref} className={className} data-testid={testid}>
      {prefix}
      {fmt}
      {suffix}
    </span>
  );
}

export default AnimatedNumber;
